import React from "react";
import { format, parseISO, isValid } from "date-fns";
import { it as itLocale, enUS as enLocale } from "date-fns/locale";
import { CalendarBlank, X } from "@phosphor-icons/react";

import { Calendar } from "@/components/ui/calendar";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { useApp } from "@/context/AppContext";

// Shared date field: a Shadcn Popover with a Calendar inside.
// - value: ISO string (or "" / null)
// - onChange: called with new ISO string or null (when cleared)
// - size: "sm" (inline, e.g. checklist rows) | "md" (form field, e.g. dialog)
export default function DateField({
  value,
  onChange,
  placeholder,
  size = "md",
  testid,
  className = "",
  align = "start",
}) {
  const { t, lang } = useApp();
  const locale = lang === "en" ? enLocale : itLocale;

  const parsed = value ? parseISO(value) : null;
  const selected = parsed && isValid(parsed) ? parsed : undefined;

  const label = selected
    ? format(selected, size === "sm" ? "dd MMM yyyy" : "d MMMM yyyy", { locale })
    : (placeholder || t.modes.organizzativa.addDeadline);

  const handleSelect = (d) => {
    if (!d) {
      onChange(null);
      return;
    }
    // Normalize to UTC midnight ISO
    const iso = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
      .toISOString();
    onChange(iso);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  const isSm = size === "sm";

  const triggerCls = isSm
    ? `inline-flex items-center gap-1.5 text-xs font-sans px-2.5 py-1 rounded-lg border border-border bg-surface transition-colors ${
        selected ? "text-ink font-semibold" : "text-ink-soft"
      } hover:border-terracotta/50 hover:text-ink`
    : `inline-flex w-full items-center justify-between gap-2 h-10 px-3 rounded-lg border border-border bg-surface font-sans text-sm transition-colors ${
        selected ? "text-ink font-semibold" : "text-ink-soft"
      } hover:border-terracotta/50`;

  return (
    <div className={`inline-flex items-center gap-1 ${isSm ? "" : "w-full"}`}>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testid}
            className={`${triggerCls} ${className}`}
          >
            <CalendarBlank size={isSm ? 13 : 16} weight="duotone" className="shrink-0 text-terracotta" />
            <span className="truncate">{label}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align={align} className="w-auto p-0" data-testid={testid ? `${testid}-popover` : undefined}>
          <Calendar
            mode="single"
            selected={selected}
            onSelect={handleSelect}
            locale={locale}
            weekStartsOn={1}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {selected && (
        <button
          type="button"
          aria-label={t.modes.organizzativa.removeDeadline}
          data-testid={testid ? `${testid}-clear` : undefined}
          onClick={clear}
          className="rounded-full p-1 text-ink-soft hover:text-terracotta hover:bg-terracotta/10 transition-colors"
        >
          <X size={isSm ? 12 : 14} weight="bold" />
        </button>
      )}
    </div>
  );
}
